import { useEffect, useRef, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiStartSubscription, apiConfirmSubscription, formatApiError } from "@/lib/api";

const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const CURRENCY = process.env.REACT_APP_PAYPAL_CURRENCY || "EUR";

/**
 * PayPal SUBSCRIPTION button. Different from the legacy
 * <PayPalButton /> which only handles one-off €2 captures — this
 * one tells PayPal to render the "subscribe" button + collects a
 * billing-agreement-id, then confirms it server-side so we extend
 * plus_until from the subscription's next_billing_time.
 *
 * Renders both PayPal AND "Pay with Debit or Credit Card" so users
 * without a PayPal account can pay as guests.
 */
export default function PayPalSubscribeButton({ planKey, onSuccess, disabled }) {
    const navigate = useNavigate();
    const containerRef = useRef(null);
    const [busy, setBusy] = useState(false);

    if (!CLIENT_ID) {
        return (
            <div data-testid="paypal-sub-disabled" className="text-xs text-[var(--vm-text-muted)] italic text-center py-2">
                PayPal isn't configured.
            </div>
        );
    }

    const createSubscription = async (data, actions) => {
        setBusy(true);
        try {
            const origin = window.location.origin;
            const { subscription_id } = await apiStartSubscription(planKey, origin);
            return subscription_id;
        } catch (e) {
            toast.error(formatApiError(e));
            setBusy(false);
            throw e;
        }
    };

    const onApprove = async (data) => {
        try {
            const res = await apiConfirmSubscription(data.subscriptionID);
            if (res.ok) {
                toast.success("Welcome to rooteed Plus 🌿 — your 4-day free trial just started.");
                if (onSuccess) onSuccess(res);
                else navigate("/billing");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const onErr = (err) => {
        // eslint-disable-next-line no-console
        console.error("PayPal subscribe error:", err);
        toast.error("Couldn't start your subscription. Try again or use card payment.");
        setBusy(false);
    };

    return (
        <div
            ref={containerRef}
            data-testid={`paypal-subscribe-${planKey}`}
            className={disabled || busy ? "opacity-60 pointer-events-none" : ""}
        >
            <PayPalScriptProvider
                options={{
                    "client-id": CLIENT_ID,
                    currency: CURRENCY,
                    intent: "subscription",  // subscriptions, not one-off captures
                    vault: true,             // PayPal must store the billing agreement
                    components: "buttons",
                    "enable-funding": "card",
                }}
            >
                <PayPalButtons
                    style={{ layout: "vertical", color: "blue", label: "subscribe", height: 44, tagline: false }}
                    createSubscription={createSubscription}
                    onApprove={onApprove}
                    onError={onErr}
                    onCancel={() => {
                        setBusy(false);
                        toast("Subscription not started.");
                    }}
                    data-testid={`paypal-subscribe-buttons-${planKey}`}
                />
            </PayPalScriptProvider>
        </div>
    );
}
